import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import {
  Users, Activity, DollarSign, Cpu, Zap, Clock, AlertTriangle,
  Gauge, TrendingUp, RefreshCw, X, Server,
} from 'lucide-react';
import { api, errorText } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';
import StatsCard from '../components/ui/StatsCard';

// ── Types (loose — mirror worker/src/routes/admin.ts) ────────────────────────
type Row = Record<string, number | string | null>;
interface OverviewData {
  users: { total: number; new_in_period: number; active_accounts: number; dau: number; wau: number; mau: number; stickiness: number };
  ai: { calls: number; tokens: number; cost_usd: number; avg_latency_ms: number; error_rate: number; cache_hit_rate: number };
  activity: { events: number; avg_latency_ms: number; error_rate: number };
  series: { events_by_day: Row[]; ai_by_day: Row[]; active_users_by_day: Row[] };
}
interface AiData {
  totals: { calls: number; tokens: number; cost_usd: number; avg_latency_ms: number; p50_latency_ms: number; p95_latency_ms: number; error_rate: number; cache_hit_rate: number };
  by_model: Row[]; by_provider: Row[]; by_feature: Row[]; by_day: Row[]; by_user: Row[];
  gateway: Record<string, unknown>;
}
interface ActivityData {
  active_users_by_day: Row[]; top_events: Row[]; top_routes: Row[];
  by_category: Row[]; by_hour: Row[]; activation_funnel: Record<string, number>;
}
interface UsersData { users: Row[]; sort: string }
interface SystemData {
  scraping: { by_status: Row[]; success: number; errors: number; success_rate: number };
  api: { total: number; error_rate: number; server_error_rate: number; slowest_routes: Row[] };
  ai: { calls: number; error_rate: number; cache_hit_rate: number; avg_latency_ms: number };
  quality: { ats_distribution: Record<string, number>; grounding: Row[]; evaluation_recommendations: Row[] };
  outcomes: Row[]; anomalies: string[];
}

type TabKey = 'overview' | 'ai' | 'users' | 'system';
const TABS: { key: TabKey; label: string; icon: React.FC<{ className?: string }> }[] = [
  { key: 'overview', label: 'Overview', icon: Activity },
  { key: 'ai', label: 'AI Usage', icon: Cpu },
  { key: 'users', label: 'Users & Behaviour', icon: Users },
  { key: 'system', label: 'System Health', icon: Server },
];
const PERIODS = [7, 30, 90, 365];
const PALETTE = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#8b5cf6', '#ec4899', '#10b981'];

// ── Formatters ───────────────────────────────────────────────────────────────
const fmt = (n: unknown) => Number(n ?? 0).toLocaleString();
const fmtUsd = (n: unknown) => `$${Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
const fmtMs = (n: unknown) => `${Number(n ?? 0).toLocaleString()} ms`;
const fmtDay = (d: unknown) => String(d ?? '').slice(5); // MM-DD

const AdminTelemetry: React.FC = () => {
  const { toast } = useToast();
  const { isDark } = useTheme();
  const [tab, setTab] = useState<TabKey>('overview');
  const [period, setPeriod] = useState(30);
  const [sort, setSort] = useState('last_active');
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [ai, setAi] = useState<AiData | null>(null);
  const [activity, setActivity] = useState<ActivityData | null>(null);
  const [usersData, setUsersData] = useState<UsersData | null>(null);
  const [system, setSystem] = useState<SystemData | null>(null);
  const [drill, setDrill] = useState<Record<string, unknown> | null>(null);

  const axis = isDark ? '#94a3b8' : '#64748b';
  const grid = isDark ? '#1e293b' : '#e2e8f0';
  const tooltipStyle = useMemo(() => ({
    backgroundColor: isDark ? '#0f172a' : '#ffffff',
    border: `1px solid ${grid}`,
    borderRadius: 12,
    color: isDark ? '#f1f5f9' : '#0f172a',
    fontSize: 12,
  }), [isDark, grid]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'overview') setOverview(await api.get<OverviewData>(`/api/admin/telemetry/overview?period=${period}`, { retry: 1 }));
      else if (tab === 'ai') setAi(await api.get<AiData>(`/api/admin/telemetry/ai?period=${period}`, { retry: 1 }));
      else if (tab === 'users') {
        const [act, usr] = await Promise.all([
          api.get<ActivityData>(`/api/admin/telemetry/activity?period=${period}`, { retry: 1 }),
          api.get<UsersData>(`/api/admin/telemetry/users?period=${period}&sort=${sort}`, { retry: 1 }),
        ]);
        setActivity(act); setUsersData(usr);
      } else if (tab === 'system') setSystem(await api.get<SystemData>(`/api/admin/telemetry/system?period=${period}`, { retry: 1 }));
    } catch (e) {
      toast.error(errorText(e, 'Failed to load telemetry'));
    } finally {
      setLoading(false);
    }
  }, [tab, period, sort, toast]);

  useEffect(() => { load(); }, [load]);

  const openUser = async (id: unknown) => {
    if (id == null) return;
    try {
      setDrill(await api.get<Record<string, unknown>>(`/api/admin/telemetry/users/${id}`));
    } catch (e) {
      toast.error(errorText(e, 'Failed to load user'));
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold text-slate-900 dark:text-white">Telemetry</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Platform-wide user activity, AI usage &amp; product health.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-xl bg-slate-100 dark:bg-navy-800 p-1">
            {PERIODS.map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                  period === p ? 'bg-white dark:bg-navy-900 text-primary-600 dark:text-primary-400 shadow-sm' : 'text-slate-500 dark:text-slate-400'
                }`}
              >
                {p}d
              </button>
            ))}
          </div>
          <button onClick={load} className="btn btn-secondary px-3 py-2" aria-label="Refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 dark:border-slate-800 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
                tab === t.key
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {loading && !overview && !ai && !activity && !system ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
        </div>
      ) : (
        <>
          {tab === 'overview' && overview && (
            <OverviewTab d={overview} axis={axis} grid={grid} tooltipStyle={tooltipStyle} />
          )}
          {tab === 'ai' && ai && <AiTab d={ai} axis={axis} grid={grid} tooltipStyle={tooltipStyle} onUser={openUser} />}
          {tab === 'users' && activity && usersData && (
            <UsersTab act={activity} usr={usersData} axis={axis} grid={grid} tooltipStyle={tooltipStyle}
              sort={sort} setSort={setSort} onUser={openUser} />
          )}
          {tab === 'system' && system && <SystemTab d={system} tooltipStyle={tooltipStyle} axis={axis} grid={grid} />}
        </>
      )}

      {drill && <UserDrawer data={drill} onClose={() => setDrill(null)} />}
    </div>
  );
};

// ── Shared bits ──────────────────────────────────────────────────────────────
interface ChartProps { axis: string; grid: string; tooltipStyle: React.CSSProperties }

const Panel: React.FC<{ title: string; children: React.ReactNode; className?: string }> = ({ title, children, className = '' }) => (
  <div className={`card p-6 ${className}`}>
    <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-4">{title}</h3>
    {children}
  </div>
);

// ── Overview ─────────────────────────────────────────────────────────────────
const OverviewTab: React.FC<ChartProps & { d: OverviewData }> = ({ d, axis, grid, tooltipStyle }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      <StatsCard icon={<Users className="w-5 h-5 text-primary-500" />} label="Total Users" value={fmt(d.users.total)} delta={`+${fmt(d.users.new_in_period)} new`} deltaType="positive" />
      <StatsCard icon={<Activity className="w-5 h-5 text-primary-500" />} label="Active (MAU)" value={fmt(d.users.mau)} delta={`${d.users.stickiness}% sticky`} />
      <StatsCard icon={<TrendingUp className="w-5 h-5 text-primary-500" />} label="Active Today" value={fmt(d.users.dau)} />
      <StatsCard icon={<Cpu className="w-5 h-5 text-primary-500" />} label="AI Calls" value={fmt(d.ai.calls)} delta={`${d.ai.error_rate}% err`} deltaType={d.ai.error_rate > 5 ? 'negative' : 'neutral'} />
      <StatsCard icon={<DollarSign className="w-5 h-5 text-primary-500" />} label="AI Cost" value={fmtUsd(d.ai.cost_usd)} />
      <StatsCard icon={<Clock className="w-5 h-5 text-primary-500" />} label="Avg AI Latency" value={fmtMs(d.ai.avg_latency_ms)} />
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Panel title="Active users per day">
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={d.series.active_users_by_day}>
              <defs>
                <linearGradient id="au" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={grid} />
              <XAxis dataKey="day" tickFormatter={fmtDay} stroke={axis} fontSize={11} />
              <YAxis stroke={axis} fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="count" stroke="#6366f1" fill="url(#au)" name="Active users" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Panel>

      <Panel title="AI spend & calls per day">
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={d.series.ai_by_day}>
              <CartesianGrid strokeDasharray="3 3" stroke={grid} />
              <XAxis dataKey="day" tickFormatter={fmtDay} stroke={axis} fontSize={11} />
              <YAxis yAxisId="l" stroke={axis} fontSize={11} />
              <YAxis yAxisId="r" orientation="right" stroke={axis} fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line yAxisId="l" type="monotone" dataKey="cost" stroke="#22c55e" name="Cost ($)" dot={false} />
              <Line yAxisId="r" type="monotone" dataKey="calls" stroke="#f59e0b" name="Calls" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Panel>
    </div>

    <Panel title="API events per day">
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={d.series.events_by_day}>
            <CartesianGrid strokeDasharray="3 3" stroke={grid} />
            <XAxis dataKey="day" tickFormatter={fmtDay} stroke={axis} fontSize={11} />
            <YAxis stroke={axis} fontSize={11} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} name="Events" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Panel>
  </div>
);

// ── AI Usage ─────────────────────────────────────────────────────────────────
const AiTab: React.FC<ChartProps & { d: AiData; onUser: (id: unknown) => void }> = ({ d, axis, grid, tooltipStyle, onUser }) => {
  const gw = d.gateway as { available?: boolean; requests?: number; tokens_total?: number; reason?: string; note?: string };
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatsCard icon={<Cpu className="w-5 h-5 text-primary-500" />} label="AI Calls" value={fmt(d.totals.calls)} />
        <StatsCard icon={<Zap className="w-5 h-5 text-primary-500" />} label="Tokens" value={fmt(d.totals.tokens)} />
        <StatsCard icon={<DollarSign className="w-5 h-5 text-primary-500" />} label="Est. Cost" value={fmtUsd(d.totals.cost_usd)} />
        <StatsCard icon={<Gauge className="w-5 h-5 text-primary-500" />} label="p95 Latency" value={fmtMs(d.totals.p95_latency_ms)} delta={`p50 ${fmt(d.totals.p50_latency_ms)}ms`} />
        <StatsCard icon={<AlertTriangle className="w-5 h-5 text-primary-500" />} label="Error Rate" value={`${d.totals.error_rate}%`} deltaType={d.totals.error_rate > 5 ? 'negative' : 'neutral'} />
        <StatsCard icon={<RefreshCw className="w-5 h-5 text-primary-500" />} label="Cache Hit" value={`${d.totals.cache_hit_rate}%`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Tokens by model">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.by_model} layout="vertical" margin={{ left: 24 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                <XAxis type="number" stroke={axis} fontSize={11} />
                <YAxis type="category" dataKey="model" stroke={axis} fontSize={10} width={150} tickFormatter={(m: string) => String(m).replace('@cf/', '').replace('anthropic/', '')} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="tokens" fill="#6366f1" radius={[0, 4, 4, 0]} name="Tokens" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Calls by provider">
          <div className="h-72 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={d.by_provider} dataKey="calls" nameKey="provider" cx="50%" cy="50%" outerRadius={90} label>
                  {d.by_provider.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>

      <Panel title="Cost by feature">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={d.by_feature}>
              <CartesianGrid strokeDasharray="3 3" stroke={grid} />
              <XAxis dataKey="feature" stroke={axis} fontSize={10} interval={0} angle={-15} textAnchor="end" height={60} />
              <YAxis stroke={axis} fontSize={11} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="cost" fill="#22c55e" radius={[4, 4, 0, 0]} name="Cost ($)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Top users by AI cost">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-400 uppercase tracking-wide">
                  <th className="py-2">User</th><th className="py-2 text-right">Calls</th><th className="py-2 text-right">Tokens</th><th className="py-2 text-right">Cost</th>
                </tr>
              </thead>
              <tbody>
                {d.by_user.map((u, i) => (
                  <tr key={i} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-navy-800 cursor-pointer" onClick={() => onUser(u.user_id)}>
                    <td className="py-2 text-slate-700 dark:text-slate-300 truncate max-w-[180px]">{String(u.email)}</td>
                    <td className="py-2 text-right">{fmt(u.calls)}</td>
                    <td className="py-2 text-right">{fmt(u.tokens)}</td>
                    <td className="py-2 text-right font-medium">{fmtUsd(u.cost)}</td>
                  </tr>
                ))}
                {d.by_user.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-slate-400">No AI usage yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="Gateway reconciliation">
          {gw?.available ? (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Gateway requests</span><span className="font-semibold">{fmt(gw.requests)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Gateway tokens</span><span className="font-semibold">{fmt(gw.tokens_total)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Instrumented calls</span><span className="font-semibold">{fmt(d.totals.calls)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Instrumented tokens</span><span className="font-semibold">{fmt(d.totals.tokens)}</span></div>
              <p className="text-xs text-slate-400 pt-2">{String(gw.note ?? '')}</p>
            </div>
          ) : (
            <div className="text-sm text-slate-500 dark:text-slate-400">
              <p className="mb-2">Live Cloudflare AI Gateway analytics unavailable.</p>
              <p className="text-xs text-slate-400">{String(gw?.reason ?? 'Not configured')}</p>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
};

// ── Users & Behaviour ────────────────────────────────────────────────────────
const FUNNEL_STEPS: { key: string; label: string }[] = [
  { key: 'signed_up', label: 'Signed up' },
  { key: 'uploaded_resume', label: 'Uploaded resume' },
  { key: 'completed_profile', label: 'Completed profile' },
  { key: 'generated_document', label: 'Generated a document' },
  { key: 'tracked_application', label: 'Tracked an application' },
  { key: 'received_offer', label: 'Received an offer' },
];

const UsersTab: React.FC<ChartProps & {
  act: ActivityData; usr: UsersData; sort: string; setSort: (s: string) => void; onUser: (id: unknown) => void;
}> = ({ act, usr, axis, grid, tooltipStyle, sort, setSort, onUser }) => {
  const funnelTop = Number(act.activation_funnel.signed_up ?? 0) || 1;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Active users & events per day">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={act.active_users_by_day}>
                <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                <XAxis dataKey="day" tickFormatter={fmtDay} stroke={axis} fontSize={11} />
                <YAxis stroke={axis} fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="events" stroke="#6366f1" fill="#6366f1" fillOpacity={0.15} name="Events" />
                <Area type="monotone" dataKey="users" stroke="#22c55e" fill="#22c55e" fillOpacity={0.2} name="Active users" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Activation funnel">
          <div className="space-y-3">
            {FUNNEL_STEPS.map((s, i) => {
              const v = Number(act.activation_funnel[s.key] ?? 0);
              const pct = Math.round((v / funnelTop) * 100);
              return (
                <div key={s.key}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-600 dark:text-slate-300">{s.label}</span>
                    <span className="text-slate-500">{fmt(v)} · {pct}%</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-slate-100 dark:bg-navy-800 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: PALETTE[i % PALETTE.length] }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Activity by hour (UTC)">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={act.by_hour}>
                <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                <XAxis dataKey="hour" stroke={axis} fontSize={10} />
                <YAxis stroke={axis} fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Events" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Usage by area">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={act.by_category}>
                <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                <XAxis dataKey="category" stroke={axis} fontSize={10} />
                <YAxis stroke={axis} fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" fill="#06b6d4" radius={[4, 4, 0, 0]} name="Events" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>

      <Panel title="Users">
        <div className="flex justify-end mb-2">
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="form-input text-xs py-1 w-40">
            <option value="last_active">Last active</option>
            <option value="cost">AI cost</option>
            <option value="tokens">Tokens</option>
            <option value="ai_calls">AI calls</option>
            <option value="events">Events</option>
            <option value="created">Newest</option>
          </select>
        </div>
        <div className="overflow-x-auto max-h-[520px]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white dark:bg-navy-900">
              <tr className="text-left text-xs text-slate-400 uppercase tracking-wide">
                <th className="py-2">User</th><th>Role</th><th className="text-right">Last active</th>
                <th className="text-right">Events</th><th className="text-right">AI calls</th>
                <th className="text-right">Cost</th><th className="text-right">Apps</th><th className="text-right">Docs</th>
              </tr>
            </thead>
            <tbody>
              {usr.users.map((u, i) => (
                <tr key={i} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-navy-800 cursor-pointer" onClick={() => onUser(u.id)}>
                  <td className="py-2 text-slate-700 dark:text-slate-300 truncate max-w-[200px]">{String(u.email)}</td>
                  <td>{u.role === 'admin' ? <span className="badge badge-primary">admin</span> : <span className="text-slate-400 text-xs">user</span>}</td>
                  <td className="text-right text-xs text-slate-500">{u.last_active ? String(u.last_active).slice(0, 16).replace('T', ' ') : '—'}</td>
                  <td className="text-right">{fmt(u.events)}</td>
                  <td className="text-right">{fmt(u.ai_calls)}</td>
                  <td className="text-right font-medium">{fmtUsd(u.cost)}</td>
                  <td className="text-right">{fmt(u.applications)}</td>
                  <td className="text-right">{fmt(u.documents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
};

// ── System Health ────────────────────────────────────────────────────────────
const SystemTab: React.FC<ChartProps & { d: SystemData }> = ({ d, tooltipStyle }) => {
  const ats = d.quality.ats_distribution || {};
  const atsData = [
    { name: 'Excellent (90+)', value: Number(ats.excellent ?? 0), fill: '#22c55e' },
    { name: 'Good (75-89)', value: Number(ats.good ?? 0), fill: '#6366f1' },
    { name: 'Fair (50-74)', value: Number(ats.fair ?? 0), fill: '#f59e0b' },
    { name: 'Poor (<50)', value: Number(ats.poor ?? 0), fill: '#ef4444' },
  ];
  return (
    <div className="space-y-6">
      {d.anomalies.length > 0 && (
        <div className="card p-4 border-l-4 border-warning-500 bg-warning-50/50 dark:bg-warning-500/5">
          <div className="flex items-center gap-2 mb-2 text-warning-700 dark:text-warning-400 font-semibold text-sm">
            <AlertTriangle className="w-4 h-4" /> Anomalies detected
          </div>
          <ul className="list-disc list-inside text-sm text-slate-600 dark:text-slate-300 space-y-1">
            {d.anomalies.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatsCard icon={<Server className="w-5 h-5 text-primary-500" />} label="Scrape Success" value={`${d.scraping.success_rate}%`} delta={`${fmt(d.scraping.success)} runs`} deltaType={d.scraping.success_rate < 80 ? 'negative' : 'positive'} />
        <StatsCard icon={<AlertTriangle className="w-5 h-5 text-primary-500" />} label="API Error Rate" value={`${d.api.error_rate}%`} delta={`${d.api.server_error_rate}% 5xx`} deltaType={d.api.error_rate > 5 ? 'negative' : 'neutral'} />
        <StatsCard icon={<Cpu className="w-5 h-5 text-primary-500" />} label="AI Error Rate" value={`${d.ai.error_rate}%`} deltaType={d.ai.error_rate > 10 ? 'negative' : 'neutral'} />
        <StatsCard icon={<Gauge className="w-5 h-5 text-primary-500" />} label="AI Cache Hit" value={`${d.ai.cache_hit_rate}%`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Generated-document ATS scores">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={atsData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {atsData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Outcome funnel">
          {d.outcomes.length > 0 ? (
            <div className="space-y-2 pt-2">
              {d.outcomes.map((o, i) => (
                <div key={i} className="flex justify-between text-sm border-b border-slate-100 dark:border-slate-800 py-1.5">
                  <span className="text-slate-600 dark:text-slate-300 capitalize">{String(o.event_type).replace('_', ' ')}</span>
                  <span className="font-semibold">{fmt(o.count)}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-400 py-8 text-center">No application outcomes recorded yet.</p>}
        </Panel>
      </div>

      <Panel title="Slowest API routes">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400 uppercase tracking-wide">
                <th className="py-2">Route</th><th className="text-right">Requests</th><th className="text-right">Avg</th><th className="text-right">Max</th>
              </tr>
            </thead>
            <tbody>
              {d.api.slowest_routes.map((r, i) => (
                <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="py-2 font-mono text-xs text-slate-700 dark:text-slate-300">{String(r.route)}</td>
                  <td className="text-right">{fmt(r.count)}</td>
                  <td className="text-right">{fmtMs(r.avg_ms)}</td>
                  <td className="text-right text-slate-500">{fmtMs(r.max_ms)}</td>
                </tr>
              ))}
              {d.api.slowest_routes.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-slate-400">No request data yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
};

// ── User drill-down drawer ───────────────────────────────────────────────────
const UserDrawer: React.FC<{ data: Record<string, unknown>; onClose: () => void }> = ({ data, onClose }) => {
  const user = (data.user ?? {}) as Record<string, unknown>;
  const agg = (data.aggregates ?? {}) as Record<string, unknown>;
  const activity = (data.recent_activity ?? []) as Row[];
  const aiRows = (data.recent_ai ?? []) as Row[];
  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative w-full max-w-lg h-full bg-white dark:bg-navy-900 shadow-2xl overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white truncate">{String(user.email ?? 'User')}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-navy-800"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-6 text-center">
          <div className="card p-3"><p className="text-lg font-bold">{fmt(agg.events)}</p><p className="text-xs text-slate-400">Events</p></div>
          <div className="card p-3"><p className="text-lg font-bold">{fmt(agg.ai_calls)}</p><p className="text-xs text-slate-400">AI calls</p></div>
          <div className="card p-3"><p className="text-lg font-bold">{fmtUsd(agg.cost)}</p><p className="text-xs text-slate-400">AI cost</p></div>
        </div>

        <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Recent AI calls</h4>
        <div className="space-y-1 mb-6">
          {aiRows.slice(0, 20).map((r, i) => (
            <div key={i} className="flex justify-between text-xs py-1 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-600 dark:text-slate-300">{String(r.feature)} · <span className="text-slate-400">{String(r.model).replace('@cf/', '').replace('anthropic/', '')}</span></span>
              <span className="text-slate-500">{fmt(r.tokens_total)}t · {fmtMs(r.latency_ms)}</span>
            </div>
          ))}
          {aiRows.length === 0 && <p className="text-xs text-slate-400">No AI calls.</p>}
        </div>

        <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Recent activity</h4>
        <div className="space-y-1">
          {activity.slice(0, 30).map((r, i) => (
            <div key={i} className="flex justify-between text-xs py-1 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-600 dark:text-slate-300 truncate max-w-[260px]">{String(r.event_type)} · <span className="font-mono text-slate-400">{String(r.route ?? r.path ?? '')}</span></span>
              <span className="text-slate-500">{String(r.status_code ?? '')}</span>
            </div>
          ))}
          {activity.length === 0 && <p className="text-xs text-slate-400">No activity.</p>}
        </div>
      </div>
    </div>
  );
};

export default AdminTelemetry;
